import classNames from "classnames";
import React, { ReactEventHandler } from "react";
import styles from "./Image.scss";
import { isEmpty } from "@core-ui/helpers/Utils";
import { getCloudinaryUrl } from "@core-ui/helpers/Image";

enum STATUS {
    FAILED,
    LAZY,
    LOADED,
    LOADING,
}

const defaultProps = {
    alt: "",
    className: "",
    imageId: "",
    imageUrl: "",
    lazy: true,
};

export interface ImageProps extends Required<typeof defaultProps> {
    height?: number;
    width?: number;
    onLoad?: ReactEventHandler;
    onError?: ReactEventHandler;
}

interface ImageState {
    status: STATUS;
}

const imageCache: { [x: string]: boolean } = {};

let io: IntersectionObserver;

const handleIntersect = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach(entry => {
        if (entry.isIntersecting === undefined || entry.isIntersecting) {
            io.unobserve(entry.target);
            (entry.target as ModifiedHTMLImageElement)._image._setState({ status: STATUS.LOADING });
        }
    });
};

if (typeof window !== "undefined" && IntersectionObserver !== undefined) {
    io = new IntersectionObserver(handleIntersect, {
        root: null,
        rootMargin: "0px",
        threshold: [0],
    });
}

interface ModifiedHTMLImageElement extends HTMLImageElement {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _image: any; // TODO: Use a better type here.
}

class Image extends React.PureComponent<ImageProps, ImageState> {
    public static defaultProps = defaultProps;

    public state = {
        status: this.getInitialStatus(this.props),
    };

    private _imageSrc? = "";
    private _image = React.createRef<ModifiedHTMLImageElement>();
    private _isMounted = false;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public UNSAFE_componentWillMount(): void {
        this._imageSrc = this._getImageSrc();

        if (this._imageSrc && imageCache[this._imageSrc] !== undefined) {
            this._setState({ status: STATUS.LOADED });
        }
    }

    public componentDidMount(): void {
        this._isMounted = true;
        this.observe();
    }

    public render(): JSX.Element | null {
        if (isEmpty(this._imageSrc)) {
            return null;
        }

        const imgClass = classNames({
            [styles.loaded]: this.state.status === STATUS.LOADED,
            [styles.loading]: this.state.status !== STATUS.LOADED,
            [styles.fadeIn]:
                this._imageSrc && imageCache[this._imageSrc] === undefined && this.state.status === STATUS.LOADED,
            [this.props.className]: !isEmpty(this.props.className),
        });

        const imageSrc = this.getSrc();

        const imageProps = imageSrc ? { src: imageSrc } : {};

        return (
            <img
                alt={this.props.alt}
                className={imgClass}
                height={this.props.height}
                onError={this.onError}
                onLoad={this.onLoad}
                ref={this._image}
                width={this.props.width}
                {...imageProps}
            />
        );
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        if (io !== undefined && this._image.current) {
            io.unobserve(this._image.current);
        }
    }

    private getInitialStatus(props: ImageProps): STATUS {
        return props.lazy ? STATUS.LAZY : STATUS.LOADING;
    }

    private observe(): void {
        if (io !== undefined && this.state.status === STATUS.LAZY && this._image.current) {
            this._image.current._image = this;
            io.observe(this._image.current);
        }
    }

    private onLoad: ReactEventHandler = event => {
        if (this._isMounted) {
            this._setState({ status: STATUS.LOADED }, () => {
                this._imageSrc && (imageCache[this._imageSrc] = true);
            });

            if (this.props.onLoad !== undefined) {
                this.props.onLoad(event);
            }
        }
    };

    private onError: ReactEventHandler = event => {
        if (this._isMounted) {
            this._setState({ status: STATUS.FAILED });
            if (this.props.onError !== undefined) {
                this.props.onError(event);
            }
        }
    };

    private _setState(state: ImageState, cb?: () => void): void {
        if (this._isMounted) {
            this.setState(state, cb);
        }
    }

    private _getImageSrc(): string {
        return this.props.imageId
            ? getCloudinaryUrl(this.props.imageId, {
                  height: this.props.height,
                  width: this.props.width,
              })
            : this.props.imageUrl;
    }

    private getSrc(): string | undefined {
        return this.state.status === STATUS.LAZY ? undefined : this._imageSrc;
    }
}

export default Image;
